import axios from 'axios';
import {ArpEntry, RouterCapabilities, RouterContext} from '../Types/type';
import {
  DmzSaveEntry,
  FirewallPrive,
  FirewallPublic,
  Lan,
  NatRule,
  PingResult,
  PublicFirewallRule,
  TracerouteHop,
  Vpn,
  WlanInterface,
  WlanMode,
} from '../Types/routerForms';

const BASE_ROUTE = 'api/routers';

export const getRouterContext = async (routerId: string): Promise<RouterContext> => {
  const response = await axios.get(`${BASE_ROUTE}/${routerId}`);
  return response.data;
};

export const getRouterCapabilities = async (routerId: string): Promise<RouterCapabilities> => {
  const response = await axios.get(`${BASE_ROUTE}/${routerId}/capabilities`);
  return response.data;
};

export const getArpTable = async (routerId: string): Promise<ArpEntry[]> => {
  const response = await axios.get(`${BASE_ROUTE}/${routerId}/arp`);
  return response.data;
};

export const runPing = async (routerId: string, destination: string): Promise<PingResult> => {
  const response = await axios.post(`${BASE_ROUTE}/${routerId}/ping`, {destination});
  return response.data;
};

export const runTraceroute = async (
  routerId: string,
  destination: string
): Promise<TracerouteHop[]> => {
  const response = await axios.post(`${BASE_ROUTE}/${routerId}/traceroute`, {destination});
  return response.data;
};

// --- LAN / DHCP ---

export const getLan = async (routerId: string): Promise<Lan> => {
  const response = await axios.get(`${BASE_ROUTE}/${routerId}/lan`);
  return response.data;
};

export const addPrivateIp = async (
  routerId: string,
  payload: {address: string; mask: string}
): Promise<void> => {
  await axios.post(`${BASE_ROUTE}/${routerId}/lan/private-ips`, payload);
};

export const removePrivateIp = async (routerId: string, id: string): Promise<void> => {
  await axios.delete(`${BASE_ROUTE}/${routerId}/lan/private-ips`, {data: {id}});
};

export interface DhcpUpdatePayload {
  id: string;
  network: string;
  rangeStart: string;
  rangeEnd: string;
  wins: string | null;
  active: boolean;
}

export const updateDhcp = async (
  routerId: string,
  payload: DhcpUpdatePayload
): Promise<void> => {
  await axios.put(`${BASE_ROUTE}/${routerId}/lan/dhcp`, payload);
};

export const addLease = async (
  routerId: string,
  payload: {ip: string; mac: string}
): Promise<void> => {
  await axios.post(`${BASE_ROUTE}/${routerId}/lan/leases`, payload);
};

export const removeLease = async (routerId: string, id: string): Promise<void> => {
  await axios.delete(`${BASE_ROUTE}/${routerId}/lan/leases`, {data: {id}});
};

export const addOption = async (
  routerId: string,
  payload: {name: string; code: string; value: string}
): Promise<void> => {
  await axios.post(`${BASE_ROUTE}/${routerId}/lan/options`, payload);
};

export const removeOption = async (routerId: string, id: string): Promise<void> => {
  await axios.delete(`${BASE_ROUTE}/${routerId}/lan/options`, {data: {id}});
};

// --- WLAN ---

export interface WifiUpdatePayload {
  id: string;
  ssid: string;
  password: string;
  active: boolean;
  mode: WlanMode | null;
}

export const getWlan = async (routerId: string): Promise<WlanInterface[]> => {
  const response = await axios.get(`${BASE_ROUTE}/${routerId}/wlan`);
  return response.data;
};

export const updateWifi = async (routerId: string, payload: WifiUpdatePayload): Promise<void> => {
  await axios.put(`${BASE_ROUTE}/${routerId}/wlan`, payload);
};

// --- VPN L2TP ---

export interface VpnAccountPayload {
  login: string;
  password: string;
  remoteAddress: string | null;
}

export const getVpn = async (routerId: string): Promise<Vpn> => {
  const response = await axios.get(`${BASE_ROUTE}/${routerId}/vpn`);
  return response.data;
};

export const updateL2tp = async (
  routerId: string,
  payload: {active: boolean; ipsecSecret: string}
): Promise<void> => {
  await axios.put(`${BASE_ROUTE}/${routerId}/vpn/l2tp`, payload);
};

export const setVpnSubnet = async (routerId: string, subnet: string): Promise<void> => {
  await axios.put(`${BASE_ROUTE}/${routerId}/vpn/subnet`, {subnet});
};

export const addVpnAccount = async (
  routerId: string,
  payload: VpnAccountPayload
): Promise<void> => {
  await axios.post(`${BASE_ROUTE}/${routerId}/vpn/accounts`, payload);
};

export const removeVpnAccount = async (routerId: string, id: string): Promise<void> => {
  await axios.delete(`${BASE_ROUTE}/${routerId}/vpn/accounts`, {data: {id}});
};

export const updateVpnPassword = async (
  routerId: string,
  id: string,
  password: string
): Promise<void> => {
  await axios.put(`${BASE_ROUTE}/${routerId}/vpn/accounts/password`, {id, password});
};

// --- Firewall ---

export const getFirewallPrive = async (routerId: string): Promise<FirewallPrive> => {
  const response = await axios.get(`${BASE_ROUTE}/${routerId}/firewall/prive`);
  return response.data;
};

export const getFirewallPublic = async (routerId: string): Promise<FirewallPublic> => {
  const response = await axios.get(`${BASE_ROUTE}/${routerId}/firewall/public`);
  return response.data;
};

export const addNatRule = async (
  routerId: string,
  payload: Omit<NatRule, 'id'>
): Promise<void> => {
  await axios.post(`${BASE_ROUTE}/${routerId}/firewall/nat`, payload);
};

export const removeNatRule = async (routerId: string, id: string): Promise<void> => {
  await axios.delete(`${BASE_ROUTE}/${routerId}/firewall/nat`, {data: {id}});
};

export const moveNatRule = async (routerId: string, id: string): Promise<void> => {
  await axios.post(`${BASE_ROUTE}/${routerId}/firewall/nat/move`, {id});
};

export const saveDmz = async (routerId: string, entries: DmzSaveEntry[]): Promise<void> => {
  await axios.put(`${BASE_ROUTE}/${routerId}/firewall/dmz`, {entries});
};

export const addPublicRule = async (
  routerId: string,
  payload: Omit<PublicFirewallRule, 'id'>
): Promise<void> => {
  await axios.post(`${BASE_ROUTE}/${routerId}/firewall/public`, payload);
};

export const removePublicRule = async (routerId: string, id: string): Promise<void> => {
  await axios.delete(`${BASE_ROUTE}/${routerId}/firewall/public`, {data: {id}});
};

export const movePublicRule = async (routerId: string, id: string): Promise<void> => {
  await axios.post(`${BASE_ROUTE}/${routerId}/firewall/public/move`, {id});
};
